document.addEventListener('DOMContentLoaded', () => {

    const wrap = document.querySelector('.collectionV2GridWrap');
    const rail = document.querySelector('.collectionFiltersV2');
    if (!wrap || !rail) return;

    const MOBILE_QUERY = window.matchMedia('(max-width: 800px)');
    const isMobile = () => MOBILE_QUERY.matches;

    /**************
     mobile sheet

     Desktop has no open/close state any more - the rail is part of the grid
     from page load. `active` on the wrap is now purely the mobile sheet's
     open flag.
     **************/

    const filterButton = document.querySelector('.collectionV2TopFilterButton');

    function openSheet() {
        wrap.classList.add('active');
        document.body.classList.add('noScroll');
        if (filterButton) {
            filterButton.classList.add('active');
            filterButton.setAttribute('aria-expanded', 'true');
            // Target the label by class: the button also holds a count badge,
            // and querySelector('span') would be a race between the two.
            const span = filterButton.querySelector('.collectionV2TopFilterButtonLabel');
            if (span) span.textContent = 'Hide filters';
        }
        // Move focus into the sheet, but onto the sheet itself rather than the
        // close button. Focusing the button drew a ring around the X on every
        // open, because a tap-opened sheet still focuses programmatically and
        // iOS paints its own focus ring for that. The container is the standard
        // dialog target anyway, and Tab from here reaches the close button
        // first, so keyboard order is unchanged.
        rail.focus();
    }

    function closeSheet() {
        wrap.classList.remove('active');
        document.body.classList.remove('noScroll');
        if (filterButton) {
            filterButton.classList.remove('active');
            filterButton.setAttribute('aria-expanded', 'false');
            const span = filterButton.querySelector('.collectionV2TopFilterButtonLabel');
            if (span) span.textContent = 'Filter & sort';
        }
    }

    filterButton?.addEventListener('click', () => {
        wrap.classList.contains('active') ? closeSheet() : openSheet();
    });

    document.querySelector('.collectionFiltersV2Close')?.addEventListener('click', closeSheet);

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && wrap.classList.contains('active')) closeSheet();
    });

    // Leaving the mobile breakpoint with the sheet open would otherwise strand
    // noScroll on the body and leave the desktop rail in an "open" state it no
    // longer has a concept of.
    MOBILE_QUERY.addEventListener('change', (e) => {
        if (!e.matches) closeSheet();
    });

    /**************
     GA4 reporting
     **************/

    function reportFilterClick(el) {
        if (typeof GA4_filter_click !== 'function') return;
        const group = el.closest('.collectionFiltersV2Item');
        // aria-label first: swatches carry no caption, so it is the only place
        // the colour name lives.
        const label = el.querySelector('.collectionFiltersV2RowLabel')
            || el.querySelector('span:last-of-type');
        GA4_filter_click({
            filter_name: (el.getAttribute('aria-label') || label?.textContent || '').trim(),
            filter_group: (group?.dataset.filterGroup
                || group?.querySelector('.collectionFiltersV2ItemHeading')?.textContent
                || '').trim()
        });
    }

    /**************
     rows and swatches

     On desktop every row is a plain link: the href already carries the full
     next-state URL, so filter state lives in the URL and back/forward work
     with no JS. On mobile the click is held and committed by Apply instead,
     so a shopper is not paying a page load per tap inside the sheet.
     **************/

    const SELECTABLE = '.collectionFiltersV2Row, .collectionFiltersV2Swatch';

    function setChecked(el, on) {
        el.classList.toggle('active', on);
        if (el.hasAttribute('role')) {
            el.setAttribute('aria-checked', String(on));
        } else if (on) {
            el.setAttribute('aria-current', 'true');
        } else {
            el.removeAttribute('aria-current');
        }
    }

    document.querySelectorAll(SELECTABLE).forEach(el => {
        el.addEventListener('click', function (e) {
            reportFilterClick(this);
            if (!isMobile()) return;

            e.preventDefault();
            const body = this.closest('.collectionFiltersV2ItemBody');
            if (this.dataset.exclusive === 'true') {
                body?.querySelectorAll(SELECTABLE).forEach(sib => setChecked(sib, false));
                setChecked(this, true);
            } else {
                setChecked(this, !this.classList.contains('active'));
            }
        });
    });

    document.querySelectorAll('.collectionFiltersV2ItemSortSelect').forEach(el => {
        el.addEventListener('click', function () {
            reportFilterClick(this);
            this.closest('.collectionFiltersV2ItemSort')
                ?.querySelectorAll('.collectionFiltersV2ItemSortSelect')
                .forEach(sib => setChecked(sib, false));
            setChecked(this, true);
        });
    });

    /**************
     capacity slider

     Single lower bound. The label updates live while dragging; the navigation
     only fires on release, so a drag across the range is one page load.
     **************/

    document.querySelectorAll('.collectionFiltersV2Range').forEach(range => {
        const input = range.querySelector('.collectionFiltersV2RangeInput');
        const output = range.querySelector('[data-range-value]');
        if (!input || !output) return;

        const unit = range.dataset.unit || '';
        const emptyLabel = range.dataset.emptyLabel || 'Any';

        const render = () => {
            const i = parseInt(input.value, 10);
            output.textContent = i > 0 ? `${values[i - 1]}+ ${unit}`.trim() : emptyLabel;

            // The filled part of the track. WebKit gives no pseudo-element for
            // it, so the CSS paints a gradient and this supplies the stop.
            // Safari needed the track hand-drawn anyway - it ignores
            // accent-color on a range - and hand-drawing loses the fill Chrome
            // used to get for free, so it comes back from here.
            const lo = Number(input.min);
            const span = Number(input.max) - lo;
            input.style.setProperty(
                '--range-fill',
                span > 0 ? ((i - lo) / span) * 100 + '%' : '0%'
            );
        };

        // The facet is a list of the discrete capacities in the collection, not
        // a range, so a lower bound is every value at or above the thumb OR-ed
        // together - the same shape the long-gun steps build server-side.
        //
        // Sorted numerically but kept as the original strings: the URL has to
        // carry Shopify's own value, so "9.0" must not become "9".
        const values = (range.dataset.values || '')
            .split(',').map(s => s.trim()).filter(Boolean)
            .sort((a, b) => parseFloat(a) - parseFloat(b));

        // The thumb is an index, not a capacity: 0 is Any, notch i is the i-th
        // value. Scaling the track by value made every position below the
        // smallest capacity behave exactly like Any - the first third of the
        // floor slider did nothing - and crowded the small safes, which cluster
        // between 0.3 and 2.84, into the bottom of the track.
        input.min = 0;
        input.max = values.length;
        input.step = 1;

        // The starting notch is read from the URL, never from the rendered input
        // value. The URL is the applied state - it is what the server filtered
        // on - and it is present whatever version of the markup is served, which
        // matters because a published theme is a snapshot: its assets and its
        // templates can be out of step, and this pairing has already produced
        // two wrong readouts. Reading the input value as an index gave
        // "0.85+ cu ft" beside a "4.6+ cu ft" pill, because setting step=1 makes
        // the browser snap 4.6 to 5 and 5 is the fifth notch. Reading a
        // data-current the older markup does not emit gave "Any capacity" on a
        // page that was plainly filtered.
        const applied = new URLSearchParams(window.location.search)
            .getAll(range.dataset.param)
            .map(parseFloat)
            .filter(n => !Number.isNaN(n));

        // No fallback to the markup: if the URL carries no value for this
        // parameter then the filter is not applied, and a data-current left over
        // in cached HTML would show a threshold the grid is not filtered by.
        const current = applied.length ? Math.min(...applied) : 0;

        input.value = current > 0
            ? values.findIndex(v => parseFloat(v) === current) + 1
            : 0;

        const atOrAbove = (i) => values.slice(i - 1);

        input.addEventListener('input', render);
        input.addEventListener('change', () => {
            if (isMobile()) return; // committed by Apply, like every other row
            const base = range.dataset.base || window.location.pathname;
            const i = parseInt(input.value, 10);
            const matched = i > 0 ? atOrAbove(i) : [];
            if (!matched.length) {
                window.location.href = base;
                return;
            }
            const sep = base.includes('?') ? '&' : '?';
            window.location.href = base + sep
                + matched.map(x => `${range.dataset.param}=${encodeURIComponent(x)}`).join('&');
        });

        render();
    });

    /**************
     price
     **************/

    const priceMin = document.querySelector('#collectionFiltersV2ItemContainerPriceInputMin');
    const priceMax = document.querySelector('#collectionFiltersV2ItemContainerPriceInputMax');

    function applyPrice() {
        const min = priceMin?.value ?? '';
        const max = priceMax?.value ?? '';
        const url = new URL(window.location.href);

        min === '' ? url.searchParams.delete('filter.v.price.gte')
            : url.searchParams.set('filter.v.price.gte', min);
        max === '' ? url.searchParams.delete('filter.v.price.lte')
            : url.searchParams.set('filter.v.price.lte', max);

        if (typeof GA4_filter_click === 'function') {
            let filter_name = 'No price selected';
            if (min && max) filter_name = `${min} - ${max}`;
            else if (min) filter_name = `Min: ${min}`;
            else if (max) filter_name = `Max: ${max}`;
            GA4_filter_click({ filter_group: 'Price', filter_name });
        }

        window.location.href = url;
    }

    document.querySelector('#collectionFiltersV2ItemContainerPriceButton')
        ?.addEventListener('click', applyPrice);

    [priceMin, priceMax].forEach(input => {
        input?.addEventListener('keydown', (e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();

            // In the sheet, committing is the bottom Apply's job. Navigating
            // from here would go straight to a URL carrying ONLY the price and
            // silently drop every row the shopper had ticked but not yet
            // applied - the sheet holds those until Apply. So Enter just closes
            // the keyboard and leaves the price to be committed with everything
            // else.
            if (isMobile()) {
                input.blur();
                return;
            }

            applyPrice();
        });
    });

    /**************
     mobile apply

     Multi-select groups contribute one parameter each per selected value, so
     this appends rather than sets - `set` collapsed Fingerprint + Keypad down
     to whichever was last in the DOM.
     **************/

    document.querySelector('.collectionFiltersV2BottomButton')?.addEventListener('click', function () {
        const url = new URL(window.location.href);

        // Drop every facet param before rebuilding from the sheet's own state,
        // otherwise deselecting a value in the sheet leaves it in the URL.
        [...url.searchParams.keys()]
            .filter(k => k.startsWith('filter.') || k === 'sort_by' || k === 'page')
            .forEach(k => url.searchParams.delete(k));

        rail.querySelectorAll(
            '.collectionFiltersV2Row.active, .collectionFiltersV2Swatch.active, .collectionFiltersV2ItemSortSelect.active'
        ).forEach(el => {
            const { param, value, values } = el.dataset;
            if (!param) return;
            // Threshold rows carry the whole set they stand for; plain rows one value.
            if (values !== undefined) {
                values.split(',').map(s => s.trim()).filter(Boolean)
                    .forEach(v => url.searchParams.append(param, v));
                return;
            }
            if (value === '' || value === undefined) return;
            url.searchParams.append(param, value);
        });

        if (priceMin?.value) url.searchParams.set('filter.v.price.gte', priceMin.value);
        if (priceMax?.value) url.searchParams.set('filter.v.price.lte', priceMax.value);

        rail.querySelectorAll('.collectionFiltersV2Range').forEach(range => {
            const input = range.querySelector('.collectionFiltersV2RangeInput');
            const i = parseInt(input?.value, 10);
            if (!(i > 0) || !range.dataset.param) return;
            // Same index semantics as the desktop slider, and the same numeric
            // sort - slice from the thumb's notch to the top.
            (range.dataset.values || '').split(',').map(s => s.trim()).filter(Boolean)
                .sort((a, b) => parseFloat(a) - parseFloat(b))
                .slice(i - 1)
                .forEach(x => url.searchParams.append(range.dataset.param, x));
        });

        window.location.href = url;
    });

});
